import { useMobileViewerUi } from "@/components/common/mobile-viewer-ui-context";
import { selectModeName } from "@/store/mode-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import {
  DollhouseIcon,
  FaroIconButton,
  LayoutSheetSmallIcon,
  RunningIcon,
  ViewOptionsIcon,
  ToolIcon,
} from "@faro-lotv/flat-ui";
import { Box, Stack, Tooltip } from "@mui/material";
import { type JSX, type ReactNode } from "react";
import { MobileViewerSheet } from "./mobile-viewer-sheet";
import { ModeSwitch } from "./mode-switch";
import { ViewSettingsToolbar } from "./view-settings-toolbar/view-settings-toolbar";

/** @returns The three persistent phone controls and their navigation/view panels. */
export function MobileViewerControls({
  cameraControls,
}: {
  cameraControls: ReactNode;
}): JSX.Element {
  const mobileUi = useMobileViewerUi();
  const mode = useAppSelector(selectModeName);
  const activeTool = useAppSelector(selectActiveTool);
  let modeLabel = "Walk";
  let modeIcon = <RunningIcon />;
  if (mode === "sheet") {
    modeLabel = "2D overview";
    modeIcon = <LayoutSheetSmallIcon />;
  } else if (mode === "overview") {
    modeLabel = "3D overview";
    modeIcon = <DollhouseIcon />;
  }
  return (
    <>
      <Box
        ref={mobileUi?.setRepresentationContainer}
        sx={{
          position: "absolute",
          top: "max(12px, env(safe-area-inset-top))",
          right: "max(12px, env(safe-area-inset-right))",
          zIndex: 2,
        }}
      />
      <Stack
        direction="row"
        aria-label="Phone viewer controls"
        sx={{
          position: "absolute",
          bottom: "max(12px, env(safe-area-inset-bottom))",
          left: "max(12px, env(safe-area-inset-left))",
          right: "max(12px, env(safe-area-inset-right))",
          justifyContent: "space-between",
          pointerEvents: "none",
          zIndex: 2,
          "& button": {
            width: 48,
            height: 48,
            borderRadius: 1,
            bgcolor: "#20252b",
            color: "white",
            pointerEvents: "auto",
            boxShadow: 2,
            "&:hover": { bgcolor: "#343b44" },
          },
        }}
      >
        <Tooltip title={`Change mode: ${modeLabel}`}>
          <FaroIconButton
            dark
            aria-label={`Change mode: ${modeLabel}`}
            aria-haspopup="dialog"
            aria-expanded={mobileUi?.surface === "mode"}
            onClick={() => mobileUi?.setSurface("mode")}
          >
            {modeIcon}
          </FaroIconButton>
        </Tooltip>
        <Stack direction="row" spacing={1}>
          <Tooltip title="View">
            <FaroIconButton
              dark
              aria-label="Open view controls"
              aria-haspopup="dialog"
              aria-expanded={mobileUi?.surface === "view"}
              onClick={() => mobileUi?.setSurface("view")}
            >
              <ViewOptionsIcon />
            </FaroIconButton>
          </Tooltip>
          <Tooltip title="Tools">
            <FaroIconButton
              dark
              aria-label="Open tools"
              aria-haspopup="dialog"
              aria-expanded={mobileUi?.surface === "tools"}
              onClick={() => mobileUi?.setSurface("tools")}
              sx={activeTool ? { outline: "2px solid #2464f5" } : undefined}
            >
              <ToolIcon />
            </FaroIconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <MobileViewerSheet name="mode" title="Mode">
        <ModeSwitch />
      </MobileViewerSheet>
      <MobileViewerSheet name="view" title="View">
        <Stack spacing={2}>
          <Box ref={mobileUi?.setViewContainer} />
          <ViewSettingsToolbar />
          {cameraControls}
        </Stack>
      </MobileViewerSheet>
    </>
  );
}
